import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function Guides() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/subscribers", { email, name, source: "guides_waitlist" });
      setDone(true);
      toast.success("You're on the waitlist. Thank you.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="guides-page" className="container-edit pt-16 md:pt-24 pb-32">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
        <div className="md:col-span-7">
          <p className="text-xs uppercase tracking-[0.3em] text-brand-terracotta mb-5">Coming Soon</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight text-brand-forest leading-[0.95]">
            Repair Guides<br />
            <em className="text-brand-moss not-italic font-light">are on their way.</em>
          </h1>
        </div>
        <div className="md:col-span-5 md:col-start-8 self-end text-brand-charcoal leading-relaxed">
          <p>
            A small library of court-safe, trauma-informed repair guides for the
            hours after contact — meltdowns, panic-lying, sibling fallout, and
            the quiet ways of rebuilding emotional safety at home. Currently
            being finalised.
          </p>
        </div>
      </div>

      {/* Single Coming Soon panel */}
      <div className="border border-brand-border bg-brand-surface p-10 md:p-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-testid="guides-comingsoon-panel">
        <div className="lg:col-span-6">
          <p className="text-xs uppercase tracking-[0.3em] text-brand-muted mb-4">The Repair Guides</p>
          <h2 className="font-serif text-3xl md:text-4xl text-brand-forest leading-tight">
            Quiet, instantly downloadable<br />guides for the 2am scroll.
          </h2>
          <p className="mt-6 text-brand-charcoal leading-relaxed">
            Each guide is short enough to read in one sitting and specific enough to
            act on tonight — verbal scripts, age-aware decoders, transition-day
            handbooks, and household repair rituals.
          </p>
          <ul className="mt-8 space-y-3 text-brand-charcoal">
            {[
              "The Transition Day Handbook",
              "The Verbal Approach Guide",
              "Developmental Maps (Early Years · Middle Childhood)",
              "The Autonomy Lab Blueprint",
            ].map((g) => (
              <li key={g} className="flex items-start gap-4">
                <span className="font-serif text-brand-terracotta text-xl leading-none mt-0.5">✦</span>
                <span>{g}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-6">
          {done ? (
            <div data-testid="guides-waitlist-success" className="bg-brand-bg border border-brand-border p-8">
              <p className="text-xs uppercase tracking-[0.3em] text-brand-terracotta mb-3">You're on the list</p>
              <h3 className="font-serif text-2xl text-brand-forest leading-tight">Thank you — quietly held.</h3>
              <p className="mt-4 text-brand-charcoal leading-relaxed">
                You will be the first to know when each guide is ready. Nothing
                else will arrive in your inbox — no marketing, no sharing of your
                email, ever.
              </p>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid="guides-waitlist-form"
              className="bg-brand-bg border border-brand-border p-8"
            >
              <p className="text-xs uppercase tracking-[0.3em] text-brand-terracotta mb-3">Be first to know</p>
              <h3 className="font-serif text-2xl text-brand-forest leading-tight mb-2">
                Join the quiet waitlist.
              </h3>
              <p className="text-sm text-brand-muted leading-relaxed mb-6">
                One short email per launch, sent only to confirm a guide is
                ready. No marketing, no sharing of your email.
              </p>
              <label className="block mb-5">
                <span className="block text-xs uppercase tracking-[0.25em] text-brand-muted mb-2">Your first name (optional)</span>
                <input
                  data-testid="guides-waitlist-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-transparent border-b border-brand-border focus:border-brand-forest outline-none py-2 text-brand-forest"
                  autoComplete="given-name"
                />
              </label>
              <label className="block mb-6">
                <span className="block text-xs uppercase tracking-[0.25em] text-brand-muted mb-2">Email *</span>
                <input
                  required
                  type="email"
                  data-testid="guides-waitlist-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-transparent border-b border-brand-border focus:border-brand-forest outline-none py-2 text-brand-forest"
                  autoComplete="email"
                />
              </label>
              <button
                type="submit"
                disabled={submitting}
                data-testid="guides-waitlist-submit"
                className="w-full inline-flex items-center justify-center px-8 py-4 bg-brand-terracotta text-brand-bg text-sm tracking-wide hover:bg-brand-terracottaDark transition-colors rounded-sm disabled:opacity-60"
              >
                {submitting ? "Saving…" : "Join the waitlist"}
              </button>
            </form>
          )}
        </div>
      </div>

      <p className="mt-10 text-sm text-brand-muted max-w-2xl leading-relaxed">
        While the guides are being finalised, 1:1 work is open.{" "}
        <Link to="/services" className="text-brand-terracotta border-b border-brand-terracotta">
          See the three private services
        </Link>{" "}
        — including a free Discovery WhatsApp Call.
      </p>
    </main>
  );
}
